import React from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import AppFallback from '../../app/components/AppFallback/AppFallback';
import { useAppSelector } from '../../common/hooks/redux';
import { AppRoute } from './routes.types';
import {
  routesByRole,
  publicRoutes,
  defaultRoutesByRole,
} from './routes.config';
import { RouteEnum } from './routeEnum';
import { paths } from './paths';
import { MainLayout } from '../layouts';

const SignIn = React.lazy(() => import('../../authorization/SignIn/SignIn'));
const SignUp = React.lazy(() => import('../../authorization/sign-up'));
const ForgotPassword = React.lazy(
  () => import('../../authorization/ForgotPassword/ForgotPassword'),
);
const ResetPassword = React.lazy(
  () => import('../../authorization/ResetPassword/ResetPassword'),
);
const ClientSignIn = React.lazy(
  () => import('../../authorization/Client/SignIn'),
);
const BillingLayout = React.lazy(
  () => import('../../domains/billing/ui/pages/BillingLayout'),
);
const BillingPlansPage = React.lazy(
  () => import('../../domains/billing/plans/ui/pages/PlansPage'),
);
const BillingPaymentOptionsPage = React.lazy(
  () => import('../../domains/billing/payment-options/ui/pages/PaymentOptionsPage'),
);
const BillingWalletsListPage = React.lazy(
  () => import('../../domains/billing/wallet/ui/pages/WalletsListPage'),
);
const BillingWalletDetailPage = React.lazy(
  () => import('../../domains/billing/wallet/ui/pages/WalletDetailPage'),
);
const BillingSubscriptionsPage = React.lazy(
  () => import('../../domains/billing/subscriptions/ui/pages/SubscriptionsPage'),
);
const BillingSubscriptionDetailPage = React.lazy(
  () => import('../../domains/billing/subscriptions/ui/pages/SubscriptionDetailPage'),
);

const flattenAppRoutes = (routes: AppRoute[]): AppRoute[] =>
  routes.flatMap(route => {
    if (route.pathKey === 'billing') {
      return [];
    }

    const childRoutes = (route.children ?? []).filter(
      child => child.route && child.element,
    );

    if (childRoutes.length > 0) {
      return childRoutes;
    }

    return route.route && route.element ? [route] : [];
  });

const AppRouter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useAppSelector(
    (state: any) => state.authorization?.user?.role || 'none',
  );
  const effectiveRole = userRole === 'none' ? 'none' : 'owner';

  React.useEffect(() => {
    if (effectiveRole !== 'none') {
      if (location.pathname === '/') {
        navigate(defaultRoutesByRole[effectiveRole]);
        return;
      }
      if (
        location.pathname.includes('login') ||
        location.pathname.includes('sign-in')
      ) {
        navigate(defaultRoutesByRole[effectiveRole]);
      }
      return;
    }

    const publicRoutePaths = publicRoutes.map(route => route.route);
    if (publicRoutePaths.includes(location.pathname)) return;

    const authPages: string[] = [
      RouteEnum.SIGN_IN,
      RouteEnum.SIGN_UP,
      RouteEnum.FORGOT_PASSWORD,
      RouteEnum.RESET_PASSWORD,
      RouteEnum.CLIENT_SIGN_IN,
    ];
    if (authPages.includes(location.pathname)) return;

    navigate(RouteEnum.SIGN_IN);
  }, [effectiveRole, location.pathname, navigate]);

  const availableRoutes = flattenAppRoutes([
    ...publicRoutes.filter(route => !String(route.route).includes('/login')),
    ...(routesByRole[effectiveRole] || []),
  ]);

  return (
    <Routes>
      <Route
        path={RouteEnum.SIGN_IN}
        element={
          <React.Suspense fallback={<AppFallback />}>
            <SignIn />
          </React.Suspense>
        }
      />
      <Route
        path={RouteEnum.SIGN_UP}
        element={
          <React.Suspense fallback={<AppFallback />}>
            <SignUp />
          </React.Suspense>
        }
      />
      <Route
        path={RouteEnum.FORGOT_PASSWORD}
        element={
          <React.Suspense fallback={<AppFallback />}>
            <ForgotPassword />
          </React.Suspense>
        }
      />
      <Route
        path={RouteEnum.RESET_PASSWORD}
        element={
          <React.Suspense fallback={<AppFallback />}>
            <ResetPassword />
          </React.Suspense>
        }
      />
      <Route
        path={RouteEnum.CLIENT_SIGN_IN}
        element={
          <React.Suspense fallback={<AppFallback />}>
            <ClientSignIn />
          </React.Suspense>
        }
      />

      <Route element={<MainLayout />}>
        {availableRoutes.map((route: AppRoute) => (
          <Route
            key={route.route}
            path={route.route}
            element={<route.element />}
          />
        ))}

        <Route
          path={`/owner${paths.billing}`}
          element={
            <React.Suspense fallback={<AppFallback />}>
              <BillingLayout />
            </React.Suspense>
          }
        >
          <Route path='plans' element={<BillingPlansPage />} />
          <Route path='payment-options' element={<BillingPaymentOptionsPage />} />
          <Route path='wallets' element={<BillingWalletsListPage />} />
          <Route path='wallets/:id' element={<BillingWalletDetailPage />} />
          <Route path='subscriptions' element={<BillingSubscriptionsPage />} />
          <Route
            path='subscriptions/:id'
            element={<BillingSubscriptionDetailPage />}
          />
        </Route>
      </Route>
    </Routes>
  );
};

export default AppRouter;
